import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Edit, Loader2, Send, Trash2 } from 'lucide-react';
import Button from '@components/common/Button';
import ConfirmationModal from '@components/common/modals/ConfirmationModal';
import EvidenceDraftModal from './EvidenceDraftModal';
import { useDraftEvidenceActions } from '@hooks/useDraftEvidenceActions';
import type { DraftEvidencePermissions } from '@app-types';

interface DraftEvidenceActionsProps {
  assessmentItemId: number;
  processName: string;
  permissions: DraftEvidencePermissions;
  onChanged?: () => void;
}

const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

/**
 * Submit / Edit / Delete for the Draft Evidence of one Process (ADO-1148),
 * used on Assessment Detail and Evidence Management. The dialogs render in a
 * portal and swallow clicks, because on Assessment Detail this sits inside the
 * clickable Process row (a <button>), which must not receive them.
 */
const DraftEvidenceActions: React.FC<DraftEvidenceActionsProps> = ({
  assessmentItemId,
  processName,
  permissions,
  onChanged
}) => {
  const [showEditor, setShowEditor] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { submitDraft, deleteDraft, isSubmitting, isDeleting } = useDraftEvidenceActions(onChanged);
  const isBusy = isSubmitting || isDeleting;

  return (
    <>
      {permissions.canSubmit && (
        <Button
          variant="primary"
          size="sm"
          disabled={isBusy}
          onClick={(e) => {
            e.stopPropagation();
            submitDraft(assessmentItemId);
          }}
          dataTestId="draft-submit-button"
        >
          {isSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Submit Evidence
        </Button>
      )}
      {permissions.canEdit && (
        <Button
          variant="secondary"
          size="sm"
          disabled={isBusy}
          onClick={(e) => {
            e.stopPropagation();
            setShowEditor(true);
          }}
          dataTestId="draft-edit-button"
        >
          <Edit size={14} />
          Edit Draft
        </Button>
      )}
      {permissions.canDelete && (
        <Button
          variant="ghost"
          size="sm"
          disabled={isBusy}
          onClick={(e) => {
            e.stopPropagation();
            setShowDeleteConfirm(true);
          }}
          dataTestId="draft-delete-button"
        >
          <Trash2 size={14} />
          Delete Draft
        </Button>
      )}

      {(showEditor || showDeleteConfirm) &&
        createPortal(
          // React events bubble through portals, so stop them before they reach the Process row.
          <div onClick={stopPropagation} onKeyDown={stopPropagation} role="presentation">
            {showEditor && (
              <EvidenceDraftModal
                assessmentItemId={assessmentItemId}
                isOpen={showEditor}
                onClose={() => setShowEditor(false)}
                onChanged={onChanged}
              />
            )}
            <ConfirmationModal
              isOpen={showDeleteConfirm}
              onClose={() => setShowDeleteConfirm(false)}
              onConfirm={() => deleteDraft(assessmentItemId, { onSettled: () => setShowDeleteConfirm(false) })}
              title="Delete Draft Evidence"
              message={`Delete the draft evidence for "${processName}"? Its files are removed too. This cannot be undone.`}
              confirmLabel="Delete Draft"
              confirmVariant="danger"
              isProcessing={isDeleting}
              dataTestId="draft-delete-confirmation"
            />
          </div>,
          document.body
        )}
    </>
  );
};

export default DraftEvidenceActions;
